import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Star, Loader2, Sparkles, Save, RefreshCcw, MessageSquare } from "lucide-react";
import {
  getReviewStats, listReviewRequests, listFollowups, updateFollowupStatus,
  listPrivateFeedback, listCustomerReviews, getReviewSettings, updateReviewSettings,
  generateAiInsights,
} from "@/lib/reviews/reviews.functions";

export const Route = createFileRoute("/_authenticated/admin/reviews")({
  head: () => ({ meta: [{ title: "Reviews — Admin" }] }),
  component: ReviewsAdmin,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="py-16 text-center"><p className="text-red-400 mb-3 text-sm">{error.message}</p>
        <button onClick={() => { router.invalidate(); reset(); }} className="bg-white text-zinc-950 px-5 py-2 rounded-full text-sm font-semibold">Retry</button>
      </div>
    );
  },
  notFoundComponent: () => <div className="text-zinc-400">Not found.</div>,
});

type Tab = "overview" | "requests" | "reviews" | "feedback" | "followups" | "insights" | "settings";

function ReviewsAdmin() {
  const [tab, setTab] = useState<Tab>("overview");
  return (
    <div className="space-y-6 max-w-7xl">
      <header>
        <p className="text-amber-300 text-xs font-semibold tracking-widest uppercase inline-flex items-center gap-1.5"><Star className="size-3.5" /> Reputation</p>
        <h1 className="font-serif text-3xl text-white mt-1">Customer Satisfaction</h1>
        <p className="text-zinc-500 text-sm mt-1">Convert happy customers into public reviews; intercept unhappy ones privately.</p>
      </header>

      <div className="flex flex-wrap gap-1 border-b border-white/10">
        {(["overview", "requests", "reviews", "feedback", "followups", "insights", "settings"] as Tab[]).map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-3 py-2 text-sm border-b-2 transition-colors ${tab === t ? "border-amber-300 text-amber-200" : "border-transparent text-zinc-400 hover:text-white"}`}>
            {t[0].toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab />}
      {tab === "requests" && <RequestsTab />}
      {tab === "reviews" && <ReviewsTab />}
      {tab === "feedback" && <FeedbackTab />}
      {tab === "followups" && <FollowupsTab />}
      {tab === "insights" && <InsightsTab />}
      {tab === "settings" && <SettingsTab />}
    </div>
  );
}

function Card({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="p-5 rounded-2xl border border-white/10 bg-white/[0.02]">
      <p className="text-[10px] tracking-widest uppercase text-zinc-500">{label}</p>
      <p className="font-serif text-2xl text-white mt-1">{value}</p>
      {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
    </div>
  );
}

function OverviewTab() {
  const fn = useServerFn(getReviewStats);
  const { data, isLoading } = useQuery({ queryKey: ["admin", "review-stats"], queryFn: () => fn(), staleTime: 30_000 });
  if (isLoading || !data) return <div className="py-16 grid place-items-center"><Loader2 className="size-5 animate-spin text-zinc-500" /></div>;
  const pct = (n: number) => `${Math.round(n * 100)}%`;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <Card label="Requests sent" value={String(data.requestsSent)} />
      <Card label="Open rate" value={pct(data.openRate)} sub={`${data.opened} opened`} />
      <Card label="Completion" value={pct(data.completionRate)} />
      <Card label="Google clicks" value={String(data.googleClicks)} />
      <Card label="Facebook clicks" value={String(data.facebookClicks)} />
      <Card label="5-star" value={pct(data.ratingPct.five)} />
      <Card label="4-star" value={pct(data.ratingPct.four)} />
      <Card label="3-star" value={pct(data.ratingPct.three)} />
      <Card label="1–2 star" value={pct(data.ratingPct.low)} />
      <Card label="Private feedback" value={String(data.privateFeedbackCount)} />
      <Card label="Outstanding follow-ups" value={String(data.followupsOpen)} sub={`${data.followupsResolved} resolved`} />
      <Card label="Avg resolution" value={data.avgResolutionHours ? `${data.avgResolutionHours.toFixed(1)}h` : "—"} />
    </div>
  );
}

function RequestsTab() {
  const fn = useServerFn(listReviewRequests);
  const { data = [], isLoading } = useQuery({ queryKey: ["admin", "review-requests"], queryFn: () => fn({ data: { limit: 200 } }) });
  if (isLoading) return <div className="py-16 grid place-items-center"><Loader2 className="size-5 animate-spin text-zinc-500" /></div>;
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-white/[0.02] text-zinc-500 text-xs uppercase tracking-wider">
          <tr><th className="text-left px-4 py-3">Sent</th><th className="text-left px-4 py-3">Customer</th><th className="text-left px-4 py-3">Trigger</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Reminders</th><th className="text-left px-4 py-3">Link</th></tr>
        </thead>
        <tbody>
          {(data as any[]).map((r) => (
            <tr key={r.id} className="border-t border-white/5">
              <td className="px-4 py-3 text-zinc-400 text-xs">{r.sent_at ? new Date(r.sent_at).toLocaleString() : new Date(r.created_at).toLocaleString()}</td>
              <td className="px-4 py-3 text-white">{r.customer_name || "—"}<div className="text-xs text-zinc-500">{r.customer_email}</div></td>
              <td className="px-4 py-3 text-zinc-400 text-xs">{r.trigger_source}</td>
              <td className="px-4 py-3"><span className="text-[11px] px-2 py-1 rounded-full bg-amber-300/10 border border-amber-300/30 text-amber-200">{r.status}</span></td>
              <td className="px-4 py-3 text-zinc-400 text-xs">{r.reminder_count}</td>
              <td className="px-4 py-3"><a href={`/review/${r.token}`} target="_blank" rel="noopener noreferrer" className="text-amber-300 text-xs underline">open</a></td>
            </tr>
          ))}
          {!data.length && <tr><td colSpan={6} className="py-12 text-center text-zinc-500 text-sm">No review requests yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function ReviewsTab() {
  const fn = useServerFn(listCustomerReviews);
  const { data = [], isLoading } = useQuery({ queryKey: ["admin", "customer-reviews"], queryFn: () => fn() });
  if (isLoading) return <div className="py-16 grid place-items-center"><Loader2 className="size-5 animate-spin text-zinc-500" /></div>;
  return (
    <div className="grid gap-3">
      {(data as any[]).map((r) => (
        <div key={r.id} className="p-4 rounded-2xl border border-white/10 bg-white/[0.02] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`size-4 ${i < r.rating ? "fill-amber-300 text-amber-300" : "text-zinc-700"}`} />)}</div>
            <div>
              <p className="text-sm text-white">{new Date(r.submitted_at).toLocaleString()}</p>
              <p className="text-xs text-zinc-500">{r.google_clicked_at ? "✓ Google" : ""} {r.facebook_clicked_at ? "✓ Facebook" : ""}</p>
            </div>
          </div>
          <span className="text-xs text-zinc-500">{r.public_consent ? "Consented to public" : "Private"}</span>
        </div>
      ))}
      {!data.length && <div className="py-12 text-center text-zinc-500 text-sm">No reviews yet.</div>}
    </div>
  );
}

function FeedbackTab() {
  const fn = useServerFn(listPrivateFeedback);
  const { data = [], isLoading } = useQuery({ queryKey: ["admin", "private-feedback"], queryFn: () => fn() });
  if (isLoading) return <div className="py-16 grid place-items-center"><Loader2 className="size-5 animate-spin text-zinc-500" /></div>;
  return (
    <div className="grid gap-3">
      {(data as any[]).map((f) => (
        <div key={f.id} className="p-5 rounded-2xl border border-white/10 bg-white/[0.02]">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              {Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`size-3.5 ${i < f.rating ? "fill-amber-300 text-amber-300" : "text-zinc-700"}`} />)}
              {f.urgency && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/10 border border-red-500/30 text-red-200 uppercase">{f.urgency}</span>}
            </div>
            <span className="text-xs text-zinc-500">{new Date(f.created_at).toLocaleString()}</span>
          </div>
          {f.comments && <p className="text-sm text-zinc-300 whitespace-pre-wrap mb-2">{f.comments}</p>}
          {f.what_could_improve && <p className="text-sm text-zinc-400 italic">Improve: {f.what_could_improve}</p>}
          <div className="text-xs text-zinc-500 mt-2 flex flex-wrap gap-3">
            {f.phone && <span>📞 {f.phone}</span>}
            {f.preferred_contact && <span>Prefers: {f.preferred_contact}</span>}
            {f.best_time_to_call && <span>When: {f.best_time_to_call}</span>}
          </div>
        </div>
      ))}
      {!data.length && <div className="py-12 text-center text-zinc-500 text-sm">No private feedback yet.</div>}
    </div>
  );
}

function FollowupsTab() {
  const fn = useServerFn(listFollowups);
  const upd = useServerFn(updateFollowupStatus);
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["admin", "followups"], queryFn: () => fn() });
  const mut = useMutation({ mutationFn: (v: any) => upd({ data: v }), onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "followups"] }) });
  if (isLoading) return <div className="py-16 grid place-items-center"><Loader2 className="size-5 animate-spin text-zinc-500" /></div>;
  const statuses = ["open", "contacted", "resolved", "closed"] as const;
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
      {statuses.map((s) => (
        <div key={s} className="rounded-2xl border border-white/10 bg-white/[0.02] p-3 min-h-[200px]">
          <p className="text-[10px] tracking-widest uppercase text-zinc-500 mb-3 px-1">{s} ({(data as any[]).filter((d) => d.status === s).length})</p>
          <div className="space-y-2">
            {(data as any[]).filter((d) => d.status === s).map((f) => (
              <div key={f.id} className="p-3 rounded-xl bg-black/40 border border-white/5">
                <div className="flex items-center gap-2 mb-1">
                  {Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`size-3 ${i < f.rating ? "fill-amber-300 text-amber-300" : "text-zinc-700"}`} />)}
                </div>
                <p className="text-xs text-white truncate">{f.customer?.customer_name || f.customer?.customer_email || "—"}</p>
                {f.feedback?.comments && <p className="text-[11px] text-zinc-400 line-clamp-3 mt-1">{f.feedback.comments}</p>}
                <select value={f.status} onChange={(e) => mut.mutate({ id: f.id, status: e.target.value })}
                  className="mt-2 w-full bg-black/60 border border-white/10 rounded-lg px-2 py-1 text-xs text-zinc-200">
                  {statuses.map((st) => <option key={st} value={st}>{st}</option>)}
                </select>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function InsightsTab() {
  const fn = useServerFn(generateAiInsights);
  const [data, setData] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-sm text-zinc-400">AI summary across all public reviews + private feedback.</p>
        <button onClick={async () => { setBusy(true); try { setData(await fn()); } finally { setBusy(false); } }}
          className="bg-amber-300 text-zinc-950 font-semibold px-4 py-2 rounded-full text-sm hover:bg-amber-200 inline-flex items-center gap-2">
          {busy ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />} Generate insights
        </button>
      </div>
      {data && (
        <div className="grid gap-4">
          {data.summary && <div className="p-5 rounded-2xl border border-amber-300/30 bg-amber-300/[0.04]"><p className="text-sm text-amber-100 whitespace-pre-wrap">{data.summary}</p></div>}
          <Cols title="Common compliments" items={data.common_compliments} />
          <Cols title="Common complaints" items={data.common_complaints} />
          <Cols title="Recurring issues" items={data.recurring_issues} />
          <Cols title="Employee mentions" items={data.employee_mentions} />
          {data.recommendations?.length > 0 && <Cols title="Recommendations" items={data.recommendations} accent />}
          <div className="grid sm:grid-cols-3 gap-3">
            {data.color_satisfaction && <Note label="Color satisfaction" value={data.color_satisfaction} />}
            {data.scheduling_issues && <Note label="Scheduling" value={data.scheduling_issues} />}
            {data.communication_issues && <Note label="Communication" value={data.communication_issues} />}
          </div>
        </div>
      )}
    </div>
  );
}
function Cols({ title, items, accent }: { title: string; items?: string[]; accent?: boolean }) {
  if (!items?.length) return null;
  return (
    <div className={`p-4 rounded-2xl border ${accent ? "border-amber-300/30 bg-amber-300/[0.04]" : "border-white/10 bg-white/[0.02]"}`}>
      <p className="text-[10px] tracking-widest uppercase text-zinc-500 mb-2">{title}</p>
      <ul className="space-y-1.5 text-sm text-zinc-200 list-disc list-inside">{items.map((i, k) => <li key={k}>{i}</li>)}</ul>
    </div>
  );
}
function Note({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-4 rounded-2xl border border-white/10 bg-white/[0.02]">
      <p className="text-[10px] tracking-widest uppercase text-zinc-500 mb-1">{label}</p>
      <p className="text-sm text-zinc-200">{value}</p>
    </div>
  );
}

function SettingsTab() {
  const getFn = useServerFn(getReviewSettings);
  const setFn = useServerFn(updateReviewSettings);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin", "review-settings"], queryFn: () => getFn() });
  const [draft, setDraft] = useState<any>(null);
  const mut = useMutation({ mutationFn: (v: any) => setFn({ data: v }), onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "review-settings"] }) });
  if (isLoading) return <div className="py-16 grid place-items-center"><Loader2 className="size-5 animate-spin text-zinc-500" /></div>;
  const s = draft ?? data ?? {};
  const set = (k: string, v: any) => setDraft({ ...s, [k]: v });
  return (
    <div className="space-y-6 max-w-3xl">
      <div className="grid gap-3">
        <p className="text-sm font-semibold text-white">Automatic triggers</p>
        {[
          ["review_trigger_payment", "Final payment recorded"],
          ["review_trigger_paid_full", "Project marked Paid in Full"],
          ["review_trigger_warranty", "Warranty becomes active"],
        ].map(([k, label]) => (
          <label key={k} className="flex items-center justify-between p-3 rounded-xl border border-white/10 bg-white/[0.02]">
            <span className="text-sm text-zinc-200">{label}</span>
            <input type="checkbox" checked={!!s[k]} onChange={(e) => set(k, e.target.checked)} className="size-5 accent-amber-300" />
          </label>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Google Review URL"><input value={s.review_google_url ?? ""} onChange={(e) => set("review_google_url", e.target.value || null)} className="input" placeholder="https://g.page/r/..." /></Field>
        <Field label="Facebook Review URL"><input value={s.review_facebook_url ?? ""} onChange={(e) => set("review_facebook_url", e.target.value || null)} className="input" placeholder="https://facebook.com/..." /></Field>
        <Field label="Public site URL (for review link emails)"><input value={s.review_public_site_url ?? ""} onChange={(e) => set("review_public_site_url", e.target.value || null)} className="input" placeholder="https://yourdomain.com" /></Field>
        <Field label="Reminder #1 (days)"><input type="number" value={s.review_reminder_days_1 ?? 3} onChange={(e) => set("review_reminder_days_1", Number(e.target.value))} className="input" /></Field>
        <Field label="Reminder #2 (days)"><input type="number" value={s.review_reminder_days_2 ?? 7} onChange={(e) => set("review_reminder_days_2", Number(e.target.value))} className="input" /></Field>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-semibold text-white inline-flex items-center gap-2"><MessageSquare className="size-4 text-amber-300" /> Email template</p>
        <input value={s.review_email_subject ?? ""} onChange={(e) => set("review_email_subject", e.target.value)} className="input" placeholder="Subject" />
        <textarea rows={10} value={s.review_email_body ?? ""} onChange={(e) => set("review_email_body", e.target.value)} className="input font-mono text-xs" />
        <p className="text-xs text-zinc-500">Variables: <code>{"{{customer_name}}"}</code> <code>{"{{company_name}}"}</code> <code>{"{{review_link}}"}</code></p>
      </div>

      <div className="flex gap-2">
        <button onClick={() => mut.mutate(s)} disabled={mut.isPending} className="bg-amber-300 text-zinc-950 font-semibold px-5 py-2.5 rounded-full text-sm hover:bg-amber-200 disabled:opacity-50 inline-flex items-center gap-2">
          {mut.isPending ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />} Save settings
        </button>
        <button onClick={() => setDraft(null)} className="border border-white/20 text-white px-5 py-2.5 rounded-full text-sm hover:bg-white/5 inline-flex items-center gap-2">
          <RefreshCcw className="size-4" /> Reset
        </button>
      </div>

      <style>{`.input { width:100%; background:rgba(0,0,0,.4); border:1px solid rgba(255,255,255,.1); border-radius:.75rem; padding:.6rem .8rem; font-size:14px; color:#fff; outline:none; }
      .input:focus { border-color: rgba(252,191,36,.5); }`}</style>
    </div>
  );
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block"><span className="text-[10px] tracking-widest uppercase text-zinc-500">{label}</span><div className="mt-1">{children}</div></label>;
}
